import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

export interface Wire {
  from: string;
  to: string;
  kind: string;
  label?: string;
  /** Drawn bright: the chosen thing's own lines. Faint otherwise. */
  strong?: boolean;
}

interface Drawn extends Wire { d: string; mx: number; my: number }

/**
 * Lines between things drawn as HTML (docs/en/43 sec.8): each end found where the page put it,
 * so a column can scroll, fold and wrap and the lines still meet the boxes. `anchor` says which
 * element an id is drawn as - the box itself, or the folded group it is hidden in.
 */
export function Wires({ root, wires, anchor, watch }: {
  root: RefObject<HTMLElement>;
  wires: Wire[];
  anchor: (id: string) => Element | null;
  /** Anything that moves the boxes: measured again when it changes. */
  watch: unknown;
}) {
  const [drawn, setDrawn] = useState<{ lines: Drawn[]; w: number; h: number }>({ lines: [], w: 0, h: 0 });
  const [tick, setTick] = useState(0);

  // A passive effect, not a layout one: a child's layout effects run before its parent's ref is set,
  // so on the first render `root.current` was still empty and nothing was ever measured or watched.
  useEffect(() => {
    const element = root.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setTick((n) => n + 1));
    observer.observe(element);
    return () => observer.disconnect();
  }, [root]);

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const box = element.getBoundingClientRect();
    const lines: Drawn[] = [];
    const seen = new Set<string>();
    for (const wire of wires) {
      const a = anchor(wire.from);
      const b = anchor(wire.to);
      if (!a || !b || a === b) continue;
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      const id = `${ra.left},${ra.top}>${rb.left},${rb.top}:${wire.kind}`;
      if (seen.has(id)) continue;
      seen.add(id);
      const ax = ra.left - box.left; const ay = ra.top - box.top + ra.height / 2;
      const bx = rb.left - box.left; const by = rb.top - box.top + rb.height / 2;
      const sameColumn = Math.abs(ra.left - rb.left) < Math.max(40, ra.width / 2);
      let d: string; let mx: number; let my: number;
      if (sameColumn) {
        const x1 = ax + ra.width; const x2 = bx + rb.width;
        const bend = 26 + Math.min(70, Math.abs(by - ay) / 6);
        const x = Math.max(x1, x2) + bend;
        d = `M ${x1} ${ay} C ${x} ${ay}, ${x} ${by}, ${x2} ${by}`;
        mx = x - bend * 0.25; my = (ay + by) / 2;
      } else if (rb.left > ra.left) {
        const x1 = ax + ra.width; const x2 = bx;
        const dx = Math.max(30, (x2 - x1) / 2);
        d = `M ${x1} ${ay} C ${x1 + dx} ${ay}, ${x2 - dx} ${by}, ${x2} ${by}`;
        mx = (x1 + x2) / 2; my = (ay + by) / 2;
      } else {
        const x1 = ax; const x2 = bx + rb.width;
        const dx = Math.max(30, (x1 - x2) / 2);
        d = `M ${x1} ${ay} C ${x1 - dx} ${ay}, ${x2 + dx} ${by}, ${x2} ${by}`;
        mx = (x1 + x2) / 2; my = (ay + by) / 2;
      }
      lines.push({ ...wire, d, mx, my });
    }
    setDrawn({ lines, w: element.scrollWidth, h: element.scrollHeight });
    // `anchor` reads the page as it is now; what changes it is in `watch`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wires, watch, tick, root]);

  return (
    <svg className="tx-wires" width={drawn.w} height={drawn.h} aria-hidden="true">
      <defs>
        <marker id="tx-wire-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
      </defs>
      {drawn.lines.filter((l) => !l.strong).map((l, i) => (
        <path key={`f${i}`} d={l.d} className={`tx-wire tx-wire-${l.kind}`} markerEnd="url(#tx-wire-arrow)" />
      ))}
      {drawn.lines.filter((l) => l.strong).map((l, i) => (
        <g key={`s${i}`} className={`tx-wire-strong tx-wire-${l.kind}`}>
          <path d={l.d} className={`tx-wire tx-wire-${l.kind} strong`} markerEnd="url(#tx-wire-arrow)" />
          {l.label && (
            <g transform={`translate(${l.mx} ${l.my})`} className="tx-wire-label">
              <rect x={-(l.label.length * 3.3 + 6)} y={-8} width={l.label.length * 6.6 + 12} height={16} rx={8} />
              <text textAnchor="middle" y={4}>{l.label}</text>
            </g>
          )}
        </g>
      ))}
    </svg>
  );
}
